import './RedtailCredentialsModal.scss'
import React from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { Modal } from '../../../../../components/Modal/Modal'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { ReactComponent as ZocksLogo } from '../../../../../assets/images/ZocksIcon-white.svg'
import { useCreateUserIntegrationMutation } from '../../../../../store/user/user'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { IntegrationServiceId } from '../../../../../common/integrations'
import { IntegrationIcon } from '../../../../../components/IntegrationIcon'

export type RedtailCredentialsFormValues = {
  username: string
  password: string
}

export interface RedtailCredentialsModalProps {
  show: boolean
  onSave: () => void
  onCancel: () => void
}

export const RedtailCredentialsModal: React.FC<RedtailCredentialsModalProps> = ({ show, onSave, onCancel }) => {
  const defaultValues = {
    username: '',
    password: '',
  }

  const form = useForm<RedtailCredentialsFormValues>({ defaultValues })
  const [createUserIntegration, { data: userIntegrationData, isLoading: isCreatingIntegration, isSuccess }] = useCreateUserIntegrationMutation()
  const accountId = useAppSelector(selectAccountId)

  const { handleSubmit } = form

  const handleSave = async (data: RedtailCredentialsFormValues) => {
    try {
      await createUserIntegration({
        accountId,
        integrationName: IntegrationServiceId.REDTAIL,
        payload: {
          username: data.username,
          password: data.password,
        },
      }).unwrap()
      onSave()
    } catch (e) {
      console.error(e)
    }
  }

  const handleOnHide = () => {
    form.reset()
    onCancel()
  }

  const body = (
    <Container>
      <Row className='zocks-pb-2'>
        <Stack direction='horizontal' className='d-flex justify-content-center align-items-center' gap={3}>
          <div className='zocks-bg-color-primary d-flex zocks-p-0.375 justify-content-center align-items-center zocks-border-0.25'>
            <ZocksLogo width='3rem' height='3rem' />
          </div>
          <i className='bi bi-arrow-left-right zocks-redtail-connect-icon'></i>
          <div className='redtail-bg-color-primary d-flex zocks-p-0.375 justify-content-center align-items-center zocks-border-0.25'>
            <IntegrationIcon serviceId={IntegrationServiceId.REDTAIL} fill='white' width='3rem' height='3rem' />
          </div>
        </Stack>
      </Row>
      <Row className='pb-3'>
        <Col>
          <h3 className='d-flex justify-content-center align-items-center'>Connect Redtail</h3>
        </Col>
      </Row>
      <Row className='pb-3'>
        <Col>
          <p className='d-flex justify-content-center align-items-center'>Please enter your Redtail credentials</p>
        </Col>
      </Row>

      <Row>
        <FormProvider {...form}>
          <RedtailCredentialsForm />
        </FormProvider>
      </Row>
    </Container>
  )

  const footer = (
    <div className='d-flex gap-3'>
      <Button variant='secondary' onClick={onCancel}>
        Cancel
      </Button>
      <Button variant='primary' onClick={handleSubmit(handleSave)} loading={isCreatingIntegration}>
        Save
      </Button>
    </div>
  )

  return <Modal show={show} onHide={handleOnHide} header={<></>} body={body} footer={footer} centered={true} />
}

const RedtailCredentialsForm: React.FC = () => {
  const { control } = useFormContext<RedtailCredentialsFormValues>()

  return (
    <Container className='d-flex flex-column gap-4 p-0'>
      <Row>
        <Col>
          <Controller
            name='username'
            control={control}
            rules={{
              required: 'Username is required.',
            }}
            render={({ field, fieldState }) => (
              <>
                <Form.Label>Username</Form.Label>
                <Form.Control id={field.name} type='text' placeholder='Enter username' {...field} isInvalid={!!fieldState.error} />
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </>
            )}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Controller
            name='password'
            control={control}
            rules={{
              required: 'Password is required.',
            }}
            render={({ field, fieldState }) => (
              <>
                <Form.Label>Password</Form.Label>
                <Form.Control id={field.name} type='password' placeholder='Enter password…' {...field} isInvalid={!!fieldState.error} />
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </>
            )}
          />
        </Col>
      </Row>
    </Container>
  )
}
